import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

// Was: searchQuery/selectedCategory lived in CartContext, which is consumed
// by 13+ components across the PoS screen (NavBar, Header, Cart, CartTable,
// CartSummary, CommissionDialog, ...). CartContext's value can't safely be
// memoized (its cart/money functions close over state and aren't
// useCallback-stable), so every single keystroke in the item search box
// re-rendered all 13+ of them - not just the 3 that actually care about
// search/category (AppBarComp, ItemCards, FloatingFooter). Moving this
// narrow, fast-changing state into its own small memoized context stops
// that fan-out without touching any cart/money logic.
//
// The text box value (searchInput) and the value the item grid actually
// filters by (searchQuery) are separate: typing updates only searchInput
// (consumed just by the search box, via useItemSearchInput) so keystrokes
// stay instant, and searchQuery follows after a short pause - so the grid
// re-filters/re-renders once per burst of typing instead of once per key.
const ItemSearchContext = createContext();
const ItemSearchInputContext = createContext();

export const useItemSearch = () => useContext(ItemSearchContext);
export const useItemSearchInput = () => useContext(ItemSearchInputContext);

const SEARCH_DEBOUNCE_MS = 150;

export function ItemSearchProvider({ children }) {
  const [searchInput, setSearchInputState] = useState("");
  const [searchQuery, setSearchQueryState] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Typing: box updates now, grid filter catches up after a pause.
  const setSearchInput = useCallback((value) => {
    setSearchInputState(value);
    clearTimeout(timerRef.current);
    if (!value) {
      // Emptied - restore the full grid straight away.
      setSearchQueryState("");
      return;
    }
    timerRef.current = setTimeout(() => setSearchQueryState(value), SEARCH_DEBOUNCE_MS);
  }, []);

  // Immediate reset of both, cancelling any pending debounced update so a
  // late timer can't bring the old text back (e.g. after adding to cart).
  const clearSearch = useCallback(() => {
    clearTimeout(timerRef.current);
    setSearchInputState("");
    setSearchQueryState("");
  }, []);

  const filterItemsByCategory = useCallback(
    (category) => {
      setSelectedCategory(category);
      clearSearch();
    },
    [clearSearch]
  );

  const value = useMemo(
    () => ({
      searchQuery,
      setSearchQuery: setSearchInput,
      clearSearch,
      selectedCategory,
      setSelectedCategory,
      filterItemsByCategory,
    }),
    [searchQuery, setSearchInput, clearSearch, selectedCategory, filterItemsByCategory]
  );

  const inputValue = useMemo(
    () => ({ searchInput, setSearchInput, clearSearch }),
    [searchInput, setSearchInput, clearSearch]
  );

  return (
    <ItemSearchContext.Provider value={value}>
      <ItemSearchInputContext.Provider value={inputValue}>
        {children}
      </ItemSearchInputContext.Provider>
    </ItemSearchContext.Provider>
  );
}
